import React, { useEffect, useMemo, useState } from 'react';
import ReactSwitch from 'react-switch';
import { merge, cloneDeep } from 'lodash';
import { Button, Checkbox, Input } from 'src/components';
import { useAlerts } from 'src/common/AlertManager';
import { useUpdateAdminUserSettingsMutation } from 'src/api/mutations/adminUserSettingsMutations';

import styles from '../styles.module.scss';

interface ImageTabProps {
  userId: number;
  userSettings: any;
}

const ImageTab: React.FC<ImageTabProps> = ({ userId, userSettings }) => {
  const { showSuccess, showAlert } = useAlerts();
  const updateSettingsMutation = useUpdateAdminUserSettingsMutation(userId);

  const serverImage = userSettings?.image;
  const [local, setLocal] = useState<any>(() => cloneDeep(serverImage) || {});
  const [errors, setErrors] = useState<any>(null);

  useEffect(() => {
    if (serverImage) setLocal(cloneDeep(serverImage));
  }, [serverImage]);

  const hasChanges = useMemo(
    () => JSON.stringify(local) !== JSON.stringify(serverImage),
    [local, serverImage],
  );

  const update = (patch: any) => setLocal((prev: any) => merge({}, prev, patch));

  const updateConversion = (id: number, patch: any) => {
    setLocal((prev: any) => {
      const next = cloneDeep(prev);
      const idx = next.conversions.findIndex((c: any) => c.id === id);
      if (idx === -1) return next;
      if (patch.key !== undefined) {
        next.conversions[idx].key = patch.key;
      }
      if (patch.size !== undefined) {
        next.conversions[idx].size = patch.size;
      }
      if (patch.value) {
        next.conversions[idx].value = { ...next.conversions[idx].value, ...patch.value };
      }
      return next;
    });
  };

  const handleKeyChange = (id: number, value: string) => {
    const existing = local.conversions.find((c: any) => c.key === value && c.id !== id);
    if (existing) {
      setErrors({ ...errors, [id]: { ...(errors?.[id]), key: `${value} already exists` } });
      return;
    }
    setErrors((prev: any) => {
      if (!prev) return prev;
      const next = { ...prev };
      if (next[id]) delete next[id].key;
      return next;
    });
    updateConversion(id, { key: value });
  };

  const handleSizeChange = (id: number, value: string) => {
    setErrors((prev: any) => {
      if (!prev) return prev;
      const next = { ...prev };
      if (next[id]) delete next[id].size;
      return next;
    });
    updateConversion(id, { size: value });
  };

  const validateSize = (id: number, value: string) => {
    if (!value) return;
    if (!value.match(/^\d+x\d+$/g)) {
      setErrors({ ...errors, [id]: { ...(errors?.[id]), size: 'Size must be in format "WIDTHxHEIGHT"' } });
    }
  };

  const handleSave = async () => {
    try {
      await updateSettingsMutation.mutateAsync({ key: 'image', value: local });
      showSuccess('Image settings saved');
    } catch {
      showAlert('Failed to save image settings');
    }
  };

  if (!serverImage) {
    return <div className={styles.emptyState}>No image settings configured for this user.</div>;
  }

  const conversions = (local.conversions || []).filter((c: any) => c.key !== 'deepzoom');
  const deepzoom = (local.conversions || []).find((c: any) => c.key === 'deepzoom');

  return (
    <div>
      {conversions.length > 0 && (
        <>
          <h4 className={styles.settingsSectionTitle}>
            Custom Image Conversions
          </h4>
          <p className={styles.sectionDescription}>Enable the conversion to create it when an image is uploaded</p>
          <div className={styles.imageConvCardGrid}>
            {conversions.map(({ key, value, size, id }: any) => (
              <div key={id} className={styles.imageConvCard}>
                <div className={styles.imageConvCardHeader}>
                  <span>Create on upload</span>
                  <ReactSwitch
                    checked={value.enabled}
                    onChange={(checked: boolean) => updateConversion(id, { value: { enabled: checked } })}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    onColor="#2e53e3"
                    width={40}
                    height={20}
                    handleDiameter={16}
                  />
                </div>
                <div className={styles.imageConvCardRow}>
                  <div className={styles.imageConvCardField}>
                    <label>Key</label>
                    <Input variant="secondary" value={key} onChange={(val) => handleKeyChange(id, val)} />
                    {errors?.[id]?.key && (
                      <span className={styles.imageConvFieldError}>{errors[id].key}</span>
                    )}
                  </div>
                  <div className={styles.imageConvCardField}>
                    <label>Size (WxH)</label>
                    <Input
                      variant="secondary"
                      value={size}
                      onChange={(val) => handleSizeChange(id, val)}
                      onBlur={() => validateSize(id, size)}
                    />
                    {errors?.[id]?.size && (
                      <span className={styles.imageConvFieldError}>{errors[id].size}</span>
                    )}
                  </div>
                </div>
                <div className={styles.imageConvCardBody}>
                  <div className={styles.imageConvCardField}>
                    <label>Watermark</label>
                    <Checkbox
                      checked={value.watermark}
                      onChange={(e) => updateConversion(id, { value: { watermark: e.target.checked } })}
                    />
                  </div>
                  <div className={styles.imageConvCardField}>
                    <label>Public</label>
                    <Checkbox
                      checked={value.public}
                      onChange={(e) => updateConversion(id, { value: { public: e.target.checked } })}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {deepzoom && (
        <>
          <h4 className={`${styles.settingsSectionTitle} ${styles.settingsSectionTitleSpaced}`}>Zoomable Image</h4>
          <div className={styles.imageConvCard}>
            <div className={styles.imageConvCardHeader}>
              <div className={styles.keyTitle}>
                <label>Key</label>
                <span className={styles.keyPill}>{deepzoom.key}</span>
              </div>
              <ReactSwitch
                checked={deepzoom.value.enabled}
                onChange={(checked: boolean) => updateConversion(deepzoom.id, { value: { enabled: checked } })}
                checkedIcon={false}
                uncheckedIcon={false}
                onColor="#2e53e3"
                width={40}
                height={20}
                handleDiameter={16}
              />
            </div>
            <div className={styles.imageConvCardBody}>
              <div className={styles.imageConvCardField}>
                <label>Size</label>
                <span className={styles.sizeBadge}>Original size</span>
              </div>
              <div className={styles.imageConvCardField}>
                <label>Watermark</label>
                <Checkbox
                  checked={deepzoom.value.watermark}
                  onChange={(e) => updateConversion(deepzoom.id, { value: { watermark: e.target.checked } })}
                />
              </div>
              <div className={styles.imageConvCardField}>
                <label>Public</label>
                <Checkbox
                  checked={deepzoom.value.public}
                  onChange={(e) => updateConversion(deepzoom.id, { value: { public: e.target.checked } })}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <div className={styles.settingsActions}>
        <Button
          mode="primary"
          size="small"
          onClick={handleSave}
          disabled={!hasChanges || updateSettingsMutation.isLoading}
        >
          Save Image Settings
        </Button>
      </div>
    </div>
  );
};

export default ImageTab;
